import { useState, useEffect, useCallback } from 'react';

/**
 * PDF表示エリアのドラッグ&パン操作を管理するカスタムフック
 *
 * PDF表示エリアをマウスドラッグでパンスクロールする機能と、
 * ドラッグでテキスト選択を行う「テキスト選択モード」の切り替えを提供する。
 *
 * @param {React.RefObject} scrollContainerRef - スクロール対象のコンテナ要素のref
 * @returns {Object} ドラッグ&パン関連の状態とハンドラ
 * @returns {boolean} returns.isDragging - ドラッグ中かどうか
 * @returns {'drag'|'select'} returns.interactionMode - 現在の操作モード
 * @returns {Function} returns.setInteractionMode - 操作モードを切り替える
 * @returns {Function} returns.handleMouseDown - マウスダウンハンドラ
 * @returns {Function} returns.handleMouseMove - マウスムーブハンドラ
 * @returns {Function} returns.handleMouseUp - マウスアップハンドラ
 * @returns {Function} returns.handleMouseLeave - マウスリーブハンドラ
 */
export const usePdfPanScroll = (scrollContainerRef) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [interactionMode, setInteractionMode] = useState('drag'); // 'drag' | 'select'

  const handleMouseDown = useCallback((e) => {
    if (interactionMode !== 'drag') return;
    if (e.button === 2) return;
    setIsDragging(true);
    setDragStart({
      x: e.clientX,
      y: e.clientY
    });
    e.preventDefault();
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
  }, [interactionMode]);

  const handleMouseMove = useCallback((e) => {
    if (interactionMode !== 'drag') return;
    if (isDragging && scrollContainerRef.current) {
      const deltaX = dragStart.x - e.clientX;
      const deltaY = dragStart.y - e.clientY;

      scrollContainerRef.current.scrollLeft += deltaX;
      scrollContainerRef.current.scrollTop += deltaY;

      setDragStart({
        x: e.clientX,
        y: e.clientY
      });

      e.preventDefault();
    }
  }, [interactionMode, isDragging, dragStart, scrollContainerRef]);

  const endDrag = useCallback(() => {
    setIsDragging(false);
    document.body.style.userSelect = '';
    document.body.style.webkitUserSelect = '';
  }, []);

  const handleMouseUp = useCallback(() => {
    if (interactionMode !== 'drag') return;
    endDrag();
  }, [interactionMode, endDrag]);

  const handleMouseLeave = useCallback(() => {
    if (interactionMode !== 'drag') return;
    endDrag();
  }, [interactionMode, endDrag]);

  // モード切替時、ドラッグ中断状態やbody.style.userSelectが残らないようにする
  useEffect(() => {
    if (interactionMode !== 'drag') {
      endDrag();
    }
  }, [interactionMode, endDrag]);

  // ウィンドウ外でマウスボタンを離す/フォーカスが外れるなど、
  // このdiv要素のイベントが発火しないケースでもドラッグ状態を確実に終了させる
  useEffect(() => {
    if (!isDragging) return undefined;
    window.addEventListener('mouseup', endDrag, true);
    window.addEventListener('blur', endDrag);
    return () => {
      window.removeEventListener('mouseup', endDrag, true);
      window.removeEventListener('blur', endDrag);
    };
  }, [isDragging, endDrag]);

  // アンマウント時、body.style.userSelectが'none'のまま残らないようにする
  useEffect(() => {
    return () => {
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
    };
  }, []);

  return {
    isDragging,
    interactionMode,
    setInteractionMode,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleMouseLeave,
  };
};

export default usePdfPanScroll;
